import {useEffect, useRef, useState} from 'react';
import {useInterval} from 'ahooks';
import type { LoginSessionAttempt } from '@/utils/login-session-confirmation';
import createWechatQR from '@/wechatUtils';
import {getWechatLoginState, getWechatQrcodeStatus} from '@/apis/login';
import i18n from '@/locales/init';

interface Props {
  tosAgreed: boolean;
  isLong: boolean;
  setIsLong: (isLong: boolean) => void;
  scanQrcode: (token: string, studentId: number, wechatId: string, wechatTicket: string, is_bind_mobile: boolean, attempt?: LoginSessionAttempt) => void;
  updateToken: (attempt?: LoginSessionAttempt)=>Promise<LoginSessionAttempt | undefined> | void;
  onSessionAttemptStart?: () => LoginSessionAttempt;
  onSessionAttemptEnd?: (attempt: LoginSessionAttempt) => void;
}

export default function WechatLogin({
  tosAgreed,
  isLong, setIsLong,
  scanQrcode,
  updateToken,
  onSessionAttemptStart,
  onSessionAttemptEnd,
}: Props) {
  const [qrcodeId, setQrcodeId] = useState('');
  const [qrError, setQrError] = useState(false);
  const sessionAttemptRef = useRef<LoginSessionAttempt | null>(null);
  const refreshingRef = useRef(false);
  const terminalRef = useRef(false);
  const pollingRef = useRef(false);
  const mountedRef = useRef(true);
  if (!sessionAttemptRef.current && onSessionAttemptStart) {
    sessionAttemptRef.current = onSessionAttemptStart();
  }

  async function refreshQRCode() {
    if (refreshingRef.current || !mountedRef.current) return;
    refreshingRef.current = true;
    const attempt = sessionAttemptRef.current || undefined;
    const container = document.getElementById('qrcode');
    if (container) container.innerHTML = '';
    setQrcodeId('');
    setQrError(false);
    try {
      const {data: {code, redirect_uri, app_id}} = await getWechatLoginState();
      if (!mountedRef.current || (attempt && !attempt.isCurrent())) {
        return;
      }
      setQrcodeId(code);
      createWechatQR('qrcode', redirect_uri, code, app_id);
    } catch (error) {
      if (mountedRef.current && (!attempt || attempt.isCurrent())) {
        console.log(error);
        setQrError(true);
      }
    } finally {
      refreshingRef.current = false;
    }
  }

  useEffect(() => {
    refreshQRCode();
    return () => {
      mountedRef.current = false;
      if (sessionAttemptRef.current?.isCurrent()) {
        onSessionAttemptEnd?.(sessionAttemptRef.current);
      }
    };
  }, []);

  const clear = useInterval(() => {
    if (!mountedRef.current || !qrcodeId || refreshingRef.current || terminalRef.current || pollingRef.current) {
      return;
    }
    const attempt = sessionAttemptRef.current || undefined;
    if (attempt && !attempt.isCurrent()) {
      return;
    }
    pollingRef.current = true;
    getWechatQrcodeStatus(qrcodeId, isLong).then(({data: {wechat_id, wechat_ticket, token, student_id, is_bind_mobile}}) => {
      if (!mountedRef.current || terminalRef.current || (attempt && !attempt.isCurrent())) {
        return;
      }
      if (wechat_id && (!attempt || attempt.isCurrent())) {
        terminalRef.current = true;
        scanQrcode(token, student_id, wechat_id, wechat_ticket || '', is_bind_mobile === 1, attempt);
        clear();
      }
    }).catch(err=>{
      if (!mountedRef.current || terminalRef.current || (attempt && !attempt.isCurrent())) {
        return;
      }
      console.log(err);
      if (err?.code === 3103) {
        if (refreshingRef.current) return;
        terminalRef.current = false;
        setQrcodeId('');
        void refreshQRCode();
      } else if (err?.msg?.match('token')){
        refreshingRef.current = true;
        void Promise.resolve(updateToken(attempt)).then(nextAttempt => {
          if (mountedRef.current && sessionAttemptRef.current === attempt && nextAttempt) {
            sessionAttemptRef.current = nextAttempt;
          }
        }).finally(() => {
          refreshingRef.current = false;
        });
      }
    }).finally(() => {
      pollingRef.current = false;
    });
  }, 2000);


  return (
    <>
      <div className="title">{i18n.t('common.dauyan_account_login_with_wechat')}</div>
      <div className="qrcode-img" id="qrcode"></div>
      {qrError ? <div role="alert">
        <p>{i18n.t('common.dauyan_account_wechat_qrcode_failed')}</p>
        <button type="button" onClick={() => { void refreshQRCode(); }}>{i18n.t('common.dauyan_account_reload_wechat_qrcode')}</button>
      </div> : null}
      <div className="flex items-center justify-center mx-auto mt-10 mb-20">
        {/* <span className='checkspan'></span> */}
        <input checked={isLong} onChange={e => setIsLong(e.target.checked)} type="checkbox" className="checkspan"/>
        <span className="checkdesc">{i18n.t('common.dauyan_account_remember_status')}</span>
      </div>
    </>
  );
}
